/* eslint-disable react/require-default-props */
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Box, IconButton, useMediaQuery, useTheme } from '@material-ui/core';
import { CancelOutlined } from '@material-ui/icons';
import _ from 'lodash';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import { EntrepreneurCompanyDetails } from '../../types/types';
import Form from '../../components/Form';
import { formikSubmitDisabled } from '../../lib/utils';
import { StyledText } from '../../components/StyledText';
import { entPublishedCompanyDetails } from '../../lib/validationSchemas';

export const unpublishedDetailsValidationSchema = Yup.object().shape({
  managementTeam: Yup.array().of(Yup.object().shape({
    name: Yup.string().typeError('name is required').trim(),
    title: Yup.string().typeError('title is required').trim(),
    intro: Yup.string().optional().nullable().trim()
  })).optional().nullable(),
  employeeCount: Yup.number().min(0).nullable(),
  bank: Yup.string().trim(),
  auditor: Yup.string().trim(),
  lawFirm: Yup.string().trim(),
  capTableManagement: Yup.string().trim(),
  hrProvider: Yup.string().trim(),
  currentInvestor: Yup.string().trim(),
  fundUsage: Yup.string().trim(),
  founderExit: Yup.boolean(),
  founderFemale: Yup.boolean(),
  founderUnderrepresented: Yup.boolean()
});

const getValidationSchema = (isPublished: boolean) => (isPublished ? entPublishedCompanyDetails : unpublishedDetailsValidationSchema);

interface ManagementInputProps {
  id: number
  nameId: string
  titleId: string
  onDelete: (id: number) => void
  deletable?: boolean
  mobileView: boolean
}

const ManagementInput = ({ id, nameId, titleId, onDelete, deletable = true, mobileView }: ManagementInputProps) => (
  <Box key={nameId} display='flex'>
    <Box>
      <TextInput
        type='text'
        fullWidth
        id={nameId}
        label={mobileView ? 'Mgmt Name' : 'Management Name'}
        placeholder=''
      />
    </Box>
    <Box margin='0 10px'>
      <TextInput
        type='text'
        fullWidth
        id={titleId}
        label={mobileView ? 'Mgmt Title' : 'Management Title'}
        placeholder=''
      />
    </Box>
    <Box margin='auto' paddingTop='35px'>
      {
        deletable
        && (
          <IconButton size='small' style={{ width: 35, height: 35 }} onClick={() => onDelete(id)}>
            <CancelOutlined />
          </IconButton>
        )
      }
    </Box>
  </Box>
);

interface EntCompanyDetailsEditProps {
  sidePanelBody: EntrepreneurCompanyDetails
  open: boolean
  isPublished: boolean
  handleClose: () => void
  handleSubmit: (value: EntrepreneurCompanyDetails) => Promise<void>
}

const EntCompanyDetailsEdit: React.FC<EntCompanyDetailsEditProps> = ({
  sidePanelBody,
  open,
  isPublished,
  handleClose,
  handleSubmit
}) => {
  const initialValues = (values: EntrepreneurCompanyDetails) => ({
    managementTeam: values.managementTeam || [],
    employeeCount: values.employeeCount === null ? undefined : values.employeeCount,
    bank: values.bank || '',
    auditor: values.auditor || '',
    lawFirm: values.lawFirm || '',
    capTableManagement: values.capTableManagement || '',
    hrProvider: values.hrProvider || '',
    currentInvestor: values.currentInvestor || '',
    fundUsage: values.fundUsage || '',
    founderExit: values.founderExit || false,
    founderFemale: values.founderFemale || false,
    founderUnderrepresented: values.founderUnderrepresented || false
  });

  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(sidePanelBody),
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit
  });

  const addManagement = () => {
    const currentTeam = _.cloneDeep(formik.values.managementTeam);
    currentTeam.push({ id: null, name: '', title: '', intro: '' });
    formik.setFieldValue('managementTeam', currentTeam, true);
  };


  const deleteManagement = (id: number) => {
    const currentTeam = _.cloneDeep(formik.values.managementTeam);
    currentTeam.splice(id, 1);
    formik.setFieldValue('managementTeam', currentTeam, true);
  };

  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Edit Company Details</StyledText>
        </DialogTitle>
        <DialogContent>
          {formik.values.managementTeam.map((v, i) => (<ManagementInput id={i} onDelete={deleteManagement} nameId={`managementTeam[${i}].name`} titleId={`managementTeam[${i}].title`} deletable={formik.values.managementTeam.length > 1} mobileView={mobileView} key={i} />))}
          <Button type='button' size='small' disableElevation variant='text' color='secondary' onClick={addManagement}>Add Management</Button>
          <TextInput
            type='number'
            fullWidth
            id='employeeCount'
            label='Number of employees'
            placeholder=''
          />
          <TextInput
            type='text'
            fullWidth
            id='bank'
            label='Bank name'
            placeholder='Chase'
          />
          <TextInput
            type='text'
            fullWidth
            id='auditor'
            label='Auditor/CPA'
            placeholder=''
          />
          <TextInput
            type='text'
            fullWidth
            id='lawFirm'
            label='Law firm'
            placeholder=''
          />
          <TextInput
            type='text'
            fullWidth
            id='hrProvider'
            label='HR Provider'
            placeholder=''
          />
          <TextInput
            type='text'
            fullWidth
            id='capTableManagement'
            label='Cap Table Management'
            placeholder=''
          />
          <TextInput
            type='text'
            fullWidth
            id='currentInvestor'
            label='Current Investor'
            placeholder='Funded by family and friend'
          />
          <TextInput
            type='text'
            fullWidth
            id='fundUsage'
            label='Use of funds'
            placeholder=''
            multiline
            rows={6}
          />
          <Box marginTop='10px'>
            <Checkbox
              id='founderExit'
              checked={formik.values.founderExit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label='Founder Previous Exit'
            />
            <Checkbox
              id='founderFemale'
              checked={formik.values.founderFemale}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label='Female Founder'
            />
            <Checkbox
              id='founderUnderrepresented'
              checked={formik.values.founderUnderrepresented}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label='Founder Underrepresented'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            type='button'
            variant='text'
            size='large'
          >
            Cancel
          </Button>
          {formik.isSubmitting ? <CircularProgress />
            : (
              <Button
                type='submit'
                variant='contained'
                disableElevation
                color='primary'
                size='large'
                disabled={formikSubmitDisabled(formik)}
              >
                Update
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EntCompanyDetailsEdit;
